import { API_SOURCE, RowOrigin, TARGET_META } from '../models/source-key.model'
import {
  DataType,
  DisplayStyle,
  GroupingRule,
  ListDisplayKeys,
  listDisplayKeys,
  ListType,
  listValueMinChars,
  listValueOptions,
  TranCategoryKey,
  TransactionFieldMetadata,
} from '../models/transaction-metadata.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import {
  COMPARISON_KEYS,
  comparisonKeyLabel,
  COMPARISON_ORIGINS,
  ComparisonOrigin,
} from '../services/transaction-metadata.adapter'
import { TransactionMetadataSource } from '../services/transaction-metadata.service'
import { TransactionValueSource } from '../services/transaction-value.service'
import { ValuePickerOption } from '../components/value-picker/value-picker'
import { derivationsOf, FieldDerivation } from './derivation'
import { asNumber, formatFieldValue } from './format-field-value'
import { BlankCellReporter, normalizeOptions } from './list-options'
import { recordIdOf } from './transaction-category-wire'
import {
  blockDetailLine,
  BLOCK_HEADINGS,
  TranCategoryResolution,
} from './tran-category'

/**
 * Pure projection from (row, metadata, values) to the Create/Edit Transaction modal's view model.
 *
 * The host assembles the inputs — it resolves the tran category, pulls the row's matched counterparts out
 * of the matching engine, and injects the two seams — and this function does all the shaping. The
 * modal component itself is dumb: it renders this object and reports button clicks.
 *
 * Nothing here calls a backend or mutates the row. Save itself is the modal's: it assembles the
 * record from this model (`transaction-save.assembly.ts`, PVAL-26440) and sends it.
 *
 * BOTH halves of the design's Req column are enforced, because both are answerable offline. The
 * ticket's Field Settings text defines the setting as "Req / Req Parameter — **required to save**",
 * so a blank `required` field refuses Save rather than merely being counted, and a
 * supplied value short of its `requiredParameter` word count refuses it too. Emptiness is entirely
 * client-side: no endpoint is needed to see that a box has nothing in it.
 *
 * What still waits for the save endpoint is whether a SUPPLIED value is *valid* — a legal
 * instrument, an account that exists, a date inside the period. That is a different question from
 * whether the field was answered at all, and it stays unanswered here; see the validation TODO in
 * the modal's `onEdit`.
 *
 * One predicate decides it: {@link requiredUnsatisfied}, which this projection's `canSave` and the
 * modal's live re-check share, so the asterisk, the footer count and the Save button cannot drift.
 */

export type EditTransactionMode = 'create' | 'edit' | 'post'

/**
 * How a field's own value stands against the source it is compared with — the status dot at the head
 * of every row, and what the counts above the grid tally.
 *
 *  - `novalue`  — nothing on this side to compare. Dominant on a create draft, which starts empty.
 *  - `match`    — a value that either agrees with the comparison, or has nothing to compare against
 *                 (an unmapped field can never read as a difference; see the MAP note in the design).
 *  - `partial`  — the external record exists and answered nothing for this field: half-answered.
 *  - `diff`     — both sides answered and disagree.
 *
 * **`partial` and `diff` require an external record.** They are statements about a Trading or
 * Custody object that is actually in hand; on an unmatched record there is no second side, so a
 * mapped field is just a value and reads `match`. See `isMapped` in the projection.
 */
export type EditTransactionFieldStatus =
  | 'novalue'
  | 'match'
  | 'partial'
  | 'diff'

/** Tally of {@link EditTransactionFieldStatus} across every shown field, for the summary bar. */
export interface EditTransactionStatusCounts {
  match: number
  diff: number
  partial: number
  novalue: number
}

/**
 * The header's provenance line: which source this record belongs to, which schema the dialog is
 * built from, and which account it sits under — "Enfusion · Listed · Account ID 505534".
 *
 * Separate fields rather than one string because the design renders the account as a chip and the
 * others as plain text, so the template needs them apart.
 */
export interface EditTransactionBreadcrumb {
  source: string
  /** The tran category's own name — "Listed", "Interest Rate Swap", "GL Entry". */
  tranCategory: string
  /**
   * Null when there is no account to name at all.
   *
   * BC-7: reads "Account ID <id>" when the id came off the record, and "Session account <id>" when it
   * stood in for one — see {@link accountLabel}. The template renders the string as given; the
   * distinction is made in the projection, not here.
   */
  account: string | null
}

export interface EditTransactionField {
  /** Unique within the modal — the metadata `field`, suffixed for the halves of a signed split. */
  id: string
  field: string
  label: string
  /** The value's shape, which is what decides the control. Carries the metadata's own name. */
  dataType: DataType
  /** How the value is formatted for display. Never decides the control — a `prettyNumber` is still
   * a number, and a `default`-styled date is still a date. */
  style: DisplayStyle
  /** Formatted for display. */
  value: string
  rawValue: unknown
  editable: boolean
  required: boolean
  /** Parameterised-required hint (e.g. a minimum word count), or null. */
  requiredHint: string | null
  /**
   * The Req Parameter behind {@link requiredHint} — the minimum word count — or null when the field
   * has no parameterised rule.
   *
   * Carried on the field, not merely read while projecting, for the same reason `comparisonMapped`
   * is: the modal re-judges the rule as the user types, and it has nothing but the field to judge
   * from. Keeping the number here is what makes the rendered hint and the footer's count two
   * readings of ONE fact instead of two independent derivations that can disagree.
   */
  requiredParameter: number | null
  listType: ListType | null
  /**
   * The field's options, normalized (PVAL-26685): one `{value, text, cells}` per static option or
   * resolved row, read by the list's `valueKey` / `displayText` / `displayColumns`. Null means
   * free-text entry: the document names no values for this field at all.
   */
  listValues: ValuePickerOption[] | null
  /** The list's `displayColumns` — the picker's header when there is more than one. */
  displayColumns: string[]
  /**
   * All three response keys, so a row a typeahead answers later is read by the same rule the
   * projection read this field's own options by. Null for a field with no list.
   */
  listKeys: ListDisplayKeys | null
  /**
   * Where this field's derivation stands (PVAL-26685): `pending` while `/resolve` has been asked,
   * `failed` when it answered an error (the row then offers a retry), `idle` otherwise — including a
   * derivation that answered `null`, which leaves the field alone and says nothing.
   */
  derivationState: DerivationState
  /**
   * The document declares a list this phase cannot resolve — a `catalog` catalogue or a `search`
   * typeahead, both owned by the resolve endpoint that is not wired yet.
   *
   * Carried SEPARATELY from {@link listValues} because `null` options cannot tell the two apart, and
   * the distinction is observable: a declared-but-unresolvable list renders a DISABLED control (there
   * is a set, you may not choose from it yet), while a field with no `listValues` renders ordinary
   * free text (there is no set, type what you like). It is deliberately not a `mode` on the field
   * either — `controlFor` must stay mode-blind, so the projection answers the question once, here.
   */
  listPending: boolean
  /**
   * Characters needed before this field's typeahead asks the resolve endpoint, or `null` when
   * the field is not searchable — which is most of them. Doubles as the test: a `lookup` field
   * with no `search` list names no upstream and stays ordinary free text.
   */
  minChars: number | null
  /** Formatted comparison value for the primary comparison source, or null when it shows nothing. */
  comparison: string | null
  /**
   * Formatted value per comparison source, keyed by origin — the three columns behind "Hide Absent
   * Sources". Null where that source says nothing for this field.
   */
  comparisons: Partial<Record<RowOrigin, string | null>>
  /**
   * Whether the comparison source names a key for this field at all.
   *
   * Carried on the field, not just used while projecting, because the status has to be re-derived
   * when the user edits — and "mapped but the source answered nothing" (partial) is not something
   * `comparison === null` can tell apart from "not mapped at all" (match).
   */
  comparisonMapped: boolean
  /** This row's standing against the comparison source. Drives the status dot and the counts. */
  status: EditTransactionFieldStatus
}

export interface EditTransactionSection {
  name: string
  fields: EditTransactionField[]
}

/**
 * One comparison column: which source it is, what it is called, and whether it answered anything at
 * all for this record.
 *
 * `absent` is what "Hide Absent Sources" hides. A source is absent when it produced no value on any
 * field — either nothing matched it, or the fields it was asked for are unmapped on its side. That
 * is a property of the record, so it is decided here; whether absent columns are shown is a viewing
 * preference and belongs to the component.
 */
export interface EditTransactionComparisonSource {
  origin: RowOrigin
  label: string
  absent: boolean
}

/**
 * One column of a collection's item editor.
 *
 * Carries the same control-deciding metadata a top-level field does — a distribution line's account
 * is a lookup and its currency an enum exactly as they would be in the main dialog — because the
 * items modal builds controls from this and nothing else.
 */
export interface EditTransactionCollectionColumn {
  field: string
  label: string
  dataType: DataType
  style: DisplayStyle
  editable: boolean
  required: boolean
  listType: ListType | null
  /** The column's options, read by the same path a top-level field's are. Null means free text. */
  listValues: ValuePickerOption[] | null
  /** As {@link EditTransactionField.displayColumns}. */
  displayColumns: string[]
  /** As {@link EditTransactionField.listKeys}. */
  listKeys: ListDisplayKeys | null
  /** As {@link EditTransactionField.listPending}: a declared list this phase cannot resolve. */
  listPending: boolean
  /** As {@link EditTransactionField.minChars}. */
  minChars: number | null
  /**
   * Whether this column adds into the collection's total — its Grouping Logic is `sum` or
   * `condSum`. Drives the Σ row under the lines; a column that does not add shows nothing there,
   * which is how a date or a memo stays blank while the amounts total.
   */
  sums: boolean
}

/** What a collection's summary row reads as once its lines have changed. */
export interface CollectionSummary {
  rawValue: unknown
  value: string
}

export interface EditTransactionCollection {
  name: string
  label: string
  /**
   * Whether the collection's OWN actions are open — the summary's `edit`. When false the items
   * modal withholds Add Item, line removal and Done, and says so.
   *
   * It says nothing about the cells inside a line: each column answers for its own `edit`, so the
   * two flags never stand in for one another. An absent value reads as editable, so a collection
   * built by hand is not locked by omission.
   */
  editable?: boolean
  /**
   * Re-runs the summary's OWN Grouping Logic over edited lines — the same aggregation that
   * produced the row when the dialog opened, so what is shown after Done is what open would have
   * shown for those lines.
   *
   * Carried as a function rather than as data because the rule, the adding columns and their
   * conditions all live in the metadata this projection has and the dialog does not. Absent, the
   * summary is left as it was.
   */
  summarise?: (rows: string[][]) => CollectionSummary
  /** The columns of one line, for the items modal. The collection's SUMMARY is not here: it is a row
   * of the dialog, in its own display group, and is found in `sections` by its field name. */
  columns: EditTransactionCollectionColumn[]
  /** One entry per line, formatted per column, in `columns` order. */
  rows: string[][]
  /**
   * The derivations that run PER LINE (PVAL-26685) — a column whose inputs read a sibling cell on
   * the same line (`scope: "row"`), like a distribution line's sub-type following its account. The
   * items modal runs these; the dialog never does.
   */
  derivations: FieldDerivation[]
  /**
   * The summary's own `dataType` (PVAL-26440). `array` is a collection the save sends as an array of
   * line objects; `object` (an FX Swap leg) is ONE nested object, whose children the save writes by
   * path instead. Absent reads as `array`, so a collection built by hand is still a collection.
   */
  dataType?: DataType
  /**
   * The key the record carries this collection under — `collectionKeyOf(summary)`, resolved once by
   * the projection. Absent reads as {@link name}, which is what every Phase 1 summary spells.
   */
  key?: string
  /**
   * For an `object` container only: each child's opening value, by its full dotted field
   * (`nearLeg.quantity`). What a create or post writes back by path.
   */
  objectValues?: Readonly<Record<string, unknown>>
  /**
   * The lines as the record carried them, RAW — every key, including the ones no column shows (a
   * payment's `Links`), so an edited line goes back with what the user could not see intact
   * (PVAL-26440). Never written to after projection.
   */
  originalLines?: Readonly<Record<string, unknown>>[]
  /**
   * Per entry of {@link rows}, which {@link originalLines} entry it started as — or `null` for a line
   * added in the dialog. Kept in step by whoever rewrites `rows` (the items modal's Done, the bulk
   * grid's Item cells); an entry past the end reads as a new line.
   */
  lineOrigins?: (number | null)[]
}

export interface EditTransactionViewModel {
  mode: EditTransactionMode
  title: string
  /** Secondary note beneath the heading; null when there is nothing to name. */
  detailLine: string | null
  /** Source · tranCategory · account, rendered under the title. Null on a blocked resolution, which has
   * no tran category to name. */
  breadcrumb: EditTransactionBreadcrumb | null
  /** True when the modal refuses to open — heading and detail line only, no fields, no Save. */
  blocked: boolean
  sections: EditTransactionSection[]
  footer: EditTransactionField[]
  collections: EditTransactionCollection[]
  /**
   * Every TOP-LEVEL field derivation this dialog composes, ready to run (PVAL-26685). A collection
   * column's per-line derivations are on its {@link EditTransactionCollection.derivations} instead.
   *
   * Resolved here rather than in the component because it is metadata work: which fields the
   * loadout composes, and which of them carry inputs. The component only decides *when* to run them.
   */
  derivations: FieldDerivation[]
  /**
   * What a `context` derivation's inputs read — the session the modal was opened in.
   *
   * `accountId` is the RAW id (`'505534'`), never the breadcrumb's chip text: it is what `/resolve`
   * is called with and what a context input sends. A map rather than a typed shape so a second
   * context key needs no signature change.
   */
  context: Readonly<Record<string, unknown>>
  valueLabel: string
  /** Comparison column header; null when no comparison column is shown (create mode). */
  comparisonLabel: string | null
  /** The three comparison columns, each flagged absent when it answered nothing for this record.
   * Empty in create mode, which has no counterpart to compare against. */
  comparisonSources: EditTransactionComparisonSource[]
  /** Tally across every shown field — grid rows, collection summaries and footer fields alike. */
  statusCounts: EditTransactionStatusCounts
  /**
   * Whether Save is offered at all, as the dialog OPENS.
   *
   * False on a blocked resolution (there is no record to save), and false while any `required` field
   * is unsatisfied — blank, or carrying a value that breaks its own parameterised rule — see
   * {@link requiredUnsatisfied}. A blank required CELL on a collection line counts too
   * ({@link collectionRequiredUnsatisfied}): the items modal already marks it as missing, so a
   * dialog that offered Save anyway would contradict a state it is rendering.
   *
   * This is the opening state only. Every edit after that re-judges the same predicate in the modal.
   */
  canSave: boolean
  rowId: string | null
  /**
   * The tran category the dialog was projected for (PVAL-26440) — what a save's `category` is decided
   * from (`saveCategory`). Null on a blocked resolution.
   */
  tranCategory?: TranCategoryKey | null
  /**
   * The id an EDIT saves under — the record's `gLEntryId`, else its `tradeId` (`recordIdOf`). Null on
   * create and post, which make a new record, and on a blocked resolution.
   */
  recordId?: string | null
}

export interface EditTransactionInput {
  mode: EditTransactionMode
  /** Resolved by the host. In create mode this is the tran category being created. */
  resolution: TranCategoryResolution
  /** The Enfusion row being edited, or the Prometheus row being posted. Absent in create mode. */
  row: WorkbenchRow | null
  /** Matched Prometheus counterparts of `row`, assembled by the host from the matching engine. */
  counterparts: WorkbenchRow[]
  metadata: TransactionMetadataSource
  values: TransactionValueSource
  /**
   * The account a dialog with no record of its own belongs to: the workbench's focused account, which
   * the host passes so a create draft opens under the account the user is working in.
   *
   * Read only when `row` carries no account — a record's own always wins. Absent, or blank, the
   * dialog has no account: no chip, and an empty `context.accountId`.
   */
  accountId?: string | null
  /**
   * Where a lookup row that lacks one of its `displayColumns` is reported, per field (PVAL-26685).
   * The dialog passes its own once-per-(field, column) log; absent, nothing is reported.
   */
  onBlankCell?: (field: string) => BlankCellReporter
}

/** Where a field's derivation stands — see {@link EditTransactionField.derivationState}. */
export type DerivationState = 'idle' | 'pending' | 'failed'

/**
 * The RAW account id the dialog is opened for (PVAL-26685) — the record's own when it carries one,
 * else the [standIn] the host names (the workbench's focused account), else `''`.
 *
 * What every `/resolve` item carries as `params.accountId`, what `trades/get` and
 * `gl/journalentries/get` read for, and what a `context` derivation's `accountId` input sends. Not
 * the breadcrumb chip, which is the same id dressed for reading ("Account ID 505534").
 *
 * With no account at all this is empty rather than a made-up one: helios refuses an empty
 * `accountId` outright, and a derivation reads it as an input that is not set, so the dialog says
 * plainly that it has no account instead of quietly asking about someone else's.
 */
export function accountIdOf(
  row: WorkbenchRow | null,
  standIn?: string | null,
): string {
  const recordAccountId = isBlank(row?.accountId)
    ? row?.fields['accountId']
    : row?.accountId
  const account = isBlank(recordAccountId) ? standIn : recordAccountId
  return isBlank(account) ? '' : String(account).trim()
}

type Line = Record<string, unknown>

/**
 * The key a COLLECTION LINE answers to.
 *
 * A field inside a collection is named by its full path in the metadata — `distributionLines.amount`
 * — because that is how the metadata layer addresses it. The line object itself is keyed by the leaf
 * alone (`amount`), since it is already inside the collection. Reading a line by the dotted path
 * therefore always missed: every item row rendered blank and every grouped sum came out zero.
 */
export const lineKey = (field: string): string =>
  field.split('.').pop() ?? field

/**
 * One edited items-modal row, back as a line the aggregators can read.
 *
 * A cell is DISPLAY text — "1,234.50" — and `aggregate` reads `Number(line[key])`, which turns
 * that into NaN and then into 0: a silently wrong total rather than an error. So numeric columns
 * are parsed here, by the same `asNumber` the formatters use; everything else passes through, and
 * a numeric cell that does not parse stays as typed so a condition on it still sees what the user
 * wrote.
 *
 * A row only carries the columns the items modal SHOWS, so a condition on a hidden column would
 * match nothing here. No current rule authors one — `distributionLines` sums plainly and its
 * condition column, `accountSubType`, is shown.
 */
const lineFromCells = (
  columns: TransactionFieldMetadata[],
  cells: string[],
): Line =>
  Object.fromEntries(
    columns.map((meta, index) => {
      const cell = cells[index]
      const numeric = meta.dataType === 'number' || meta.dataType === 'integer'
      return [lineKey(meta.field), (numeric ? asNumber(cell) : null) ?? cell]
    }),
  )

/**
 * The key a COLLECTION answers to — on the row that carries it, and on the columns that belong to it.
 *
 * An `arraySummary` field names its collection explicitly and falls back to its own field name,
 * which is how every summary in the Phase 1 config spells it. That key must be derived ONCE and then
 * used as the single selector everywhere: the row's array is read by it, the collection's
 * `arrayField` columns are matched on it, and the summary aggregates over those columns.
 *
 * Deriving it twice — `collection ?? field` in one place and a bare `field` in another — agrees only
 * while every summary spells the two the same, and diverges SILENTLY the first time one does not:
 * no column matches the summary, so the aggregate falls through to the item count and a GL cash
 * total renders as a line COUNT instead of a sum. No exception, no failing test. The `?? field`
 * fallback exists precisely for the divergent case, so the divergent case is the one that must work.
 */
export const collectionKeyOf = (meta: TransactionFieldMetadata): string =>
  meta.collection ?? meta.field

/**
 * Read a field's value out of a record, following a dotted name into a NESTED OBJECT.
 *
 * `nearLeg.forwardDate` is not a column of a collection — an FX Swap has exactly one near leg and
 * one far leg, and Enfusion types both as `FXSwapLegInfo` rather than as an array (every genuine
 * array in that API renders as `< Type > array`; these are bare type links). So the dotted name is
 * a PATH: `{ nearLeg: { forwardDate: '2026-07-17' } }`.
 *
 * A plain `fields[meta.field]` lookup would ask for the literal key `"nearLeg.forwardDate"` and
 * always miss. Undotted names are unaffected — the loop runs once and returns the same value the
 * flat lookup did.
 *
 * Exported so the save assembly's `writePath` (PVAL-26440) can be tested as its inverse.
 */
export const readPath = (
  source: Record<string, unknown> | undefined,
  field: string,
): unknown =>
  field
    .split('.')
    .reduce<unknown>(
      (value, key) =>
        value && typeof value === 'object'
          ? (value as Record<string, unknown>)[key]
          : undefined,
      source,
    )

/**
 * What this layer counts as "no value".
 *
 * Exported because the bulk grid's opening scan judges required-ness over raw values without
 * projecting them, and the two must agree on what empty means.
 */
export const isBlank = (value: unknown): boolean =>
  value == null || (typeof value === 'string' && value.trim() === '')

/** The source's display name, from the RPS source name the panel already resolved. */
const sourceLabelOf = (source: string | undefined): string => {
  if (!source) return API_SOURCE.name
  return source.charAt(0).toUpperCase() + source.slice(1).toLowerCase()
}

const isPrometheus = (origin: RowOrigin): boolean =>
  origin === 'cust' || origin === 'trad'

/** Which of the two Prometheus keys a counterpart row answers to. */
const comparisonKeyFor = (
  meta: TransactionFieldMetadata,
  origin: RowOrigin,
): string | undefined =>
  origin === 'trad' ? meta.comparison?.tradingKey : meta.comparison?.custodyKey

/**
 * Read one datapoint off a source row, by the key the metadata layer names for it.
 *
 * A row carries its datapoints in TWO places, and a comparison key can legitimately name either:
 *
 *  - modeled catalog fields land in `row.fields`, keyed by the catalog's `field`;
 *  - Prometheus's unmodeled AggregateInformation blob stays in `row.aggregateInfo`, keyed by the
 *    blob key VERBATIM — `AggregateInfoService.discoverColumns` publishes one dynamic column per
 *    blob key with `field` set to that key, never prefixed or transformed, because the catalog
 *    contract is that `field` is the only join key. So a blob key IS a datapoint key.
 *
 * Every Prometheus comparison key the Phase 1 metadata names (`Trade_Date`, `Notional_Quantity`, …)
 * is a blob key, and reading them out of `row.fields` alone is what made all three comparison read
 * paths — the Post-Enfusion seed, the primary comparison cell, and the per-source columns — resolve
 * `undefined` on every row the adapter actually produces. Nothing failed: the dialog just rendered
 * blank where it should have rendered the counterpart's value, and the specs that "proved" it worked
 * had hand-built rows carrying the keys in `fields`, a shape `rowFromApi` never emits.
 *
 * The modeled map is consulted first — it is the catalog's own namespace, so a key Prometheus later
 * models properly starts resolving there with no metadata edit — and the blob is the fallback.
 *
 * **This precedence is correct for a CLEARWATER key and wrong for a PROMETHEUS one.** See
 * {@link prometheusValueOf}, which the Prometheus read paths use instead.
 */
const sourceValueOf = (row: WorkbenchRow, key: string): unknown =>
  key in row.fields ? row.fields[key] : row.aggregateInfo?.[key]

/** Read a datapoint a PROMETHEUS comparison key names — the aggregate-info blob first. */
const prometheusValueOf = (row: WorkbenchRow, key: string): unknown =>
  row.aggregateInfo && key in row.aggregateInfo
    ? row.aggregateInfo[key]
    : row.fields[key]

/**
 * The value a field opens with on a record, before any edit.
 *
 * Exported because the bulk grid's opening scan has to answer "is this required field blank?" over
 * tens of thousands of rows WITHOUT projecting them (a full projection costs ~0.5ms and ~34KB per
 * row). The scan reads through this function rather than reimplementing the two rules, so the two
 * cannot drift: a reimplementation that missed the dotted-path read, or the Prometheus blob
 * precedence, would under-count missing values and offer Save on a record that cannot be saved.
 *
 * `create` is deliberately not handled here — its values come from the authored seed map, which is
 * the caller's to supply, and no bulk flow creates.
 */
export function openingRawValue(
  meta: TransactionFieldMetadata,
  row: WorkbenchRow,
  mode: Extract<EditTransactionMode, 'edit' | 'post'>,
): unknown {
  if (mode === 'post') {
    // Seeded from the Prometheus row through the comparison mapping — the same authoring-time key
    // translation, read in the other direction.
    if (!isPrometheus(row.origin)) return undefined
    const key = comparisonKeyFor(meta, row.origin)
    return key ? prometheusValueOf(row, key) : undefined
  }
  // Dotted names are paths into a nested object (`nearLeg.forwardDate`), not literal keys.
  return readPath(row.fields, meta.field)
}

const requiredHintFor = (meta: TransactionFieldMetadata): string | null => {
  if (!meta.settings.required || meta.settings.requiredParameter == null) {
    return null
  }
  return `Minimum ${meta.settings.requiredParameter} words`
}

/**
 * A field's options — whichever mode put them there.
 *
 * It asked for `static` and nothing else, which was right while `static` was the only mode that
 * could carry options. A `catalog` field now carries them too, once `dialog-metadata/resolve` has
 * answered and {@link HttpDialogListValuesService} has merged them in (PVAL-26443). Keying off the
 * PRESENCE of options rather than off the mode is what makes that merge enough on its own: no
 * consumer learns a second way to have a list, and a mode this build has never heard of resolves
 * the day it starts carrying options.
 *
 * `?? null` rather than a truthiness test, so an authored-but-empty `static` list still answers
 * `[]` — "a closed set with nothing in it" is not the same fact as "no list yet", and only the
 * second one is pending.
 *
 * Exported because the dialog only renders the document's `shown` fields, so this is the only place
 * every one of the 38 `static` fields can be asserted against the real catalogue.
 */
export const listOptionsOf = (
  meta: Pick<TransactionFieldMetadata, 'listValues'>,
  onBlank?: BlankCellReporter,
): ValuePickerOption[] | null => {
  const records = listValueOptions(meta.listValues)
  const keys = listDisplayKeys(meta.listValues)
  return records && keys ? normalizeOptions(records, keys, onBlank) : null
}

/** A list's `displayColumns`, defaulted — `[]` for a field with no list. */
export const displayColumnsOf = (
  meta: Pick<TransactionFieldMetadata, 'listValues'>,
): string[] => listDisplayKeys(meta.listValues)?.displayColumns ?? []

/**
 * Whether the document declares a list whose options are not here yet.
 *
 * "Declared, and carrying no options" rather than "catalog or search": a mode this build has never
 * heard of is also a list it cannot show, and disabling the control is the safe reading of that —
 * while a `catalog` field whose options HAVE arrived is no longer waiting for anything. A field with
 * no `listValues` is NOT pending; it has no list to wait for, and stays free text.
 */
export const listPendingOf = (
  meta: Pick<TransactionFieldMetadata, 'listValues'>,
): boolean =>
  meta.listValues != null && listValueOptions(meta.listValues) === null

/**
 * How many words a value carries — whitespace-separated runs, nothing cleverer.
 *
 * A non-string value has no word count to speak of and answers 0: the parameterised rule is a
 * string rule (the design's only Req Parameter is Notes' minimum word count), and inventing a count
 * for a number would make a numeric field fail a rule that was never about it.
 */
export const wordCount = (value: unknown): number => {
  if (typeof value !== 'string') return 0
  const words = value.trim()
  return words === '' ? 0 : words.split(/\s+/).length
}

/**
 * Whether a field's own parameterised Req rule is broken by the value it is SHOWING.
 *
 * This is the `requiredParameter` half of the Req column on its own: a minimum word count, decided
 * entirely by the string in front of you — unlike "is this a legal instrument", which is the save
 * endpoint's judgement (see the validation TODO in the modal's `onEdit`, which this deliberately
 * does not reverse).
 *
 * A BLANK is never a rule violation *here*. A blank is judged by the other half — bare `required` —
 * and counting it in both places would double it in the footer note. The two halves are combined
 * once, in {@link requiredUnsatisfied}, which is what every surface actually reads.
 *
 * Exported because the hint that STATES the rule ("Minimum 3 words") is the thing that has to show
 * the rule broken, and only this half of the verdict says anything about that hint.
 */
export function requiredRuleUnmet(
  field: Pick<
    EditTransactionField,
    'required' | 'requiredParameter' | 'rawValue'
  >,
): boolean {
  if (!field.required || field.requiredParameter == null) return false
  if (isBlank(field.rawValue)) return false
  return wordCount(field.rawValue) < field.requiredParameter
}

/**
 * Whether a field is short of what saving it would need — blank-and-required, or a supplied value
 * that breaks its own parameterised rule.
 *
 * THE Req verdict. The asterisk's unsatisfied state, the footer's count and the Save button are all
 * read off this one predicate, so a dialog cannot report itself savable while a field it is
 * rendering as incomplete is incomplete. Each field contributes at most once: a blank is counted as
 * missing and never also as a broken rule.
 *
 * A blank blocks Save because "Req — required to save" is what the setting means, and emptiness is
 * the one thing about a value that needs no backend to judge. That is narrower than validation: a
 * field that is filled in is accepted here whether or not the value is *legal*, which is the save
 * endpoint's question and stays open.
 *
 * One exception, and it follows from that same rule: a field whose list is declared but unresolvable
 * ({@link EditTransactionField.listPending} — a `catalog` or `search` list the resolve endpoint owns)
 * renders DISABLED, so its blank is not something anyone can answer offline. Counting it would put the
 * dialog in a dead end — a "1 required value missing" note pointing at a control the user physically
 * cannot fill, and on a required one a Save button that can never unblock. The asterisk still marks
 * the field required, because it IS required to save; what this predicate stops claiming is that the
 * shortfall is the user's to fix in this phase (PVAL-26493).
 */
export function requiredUnsatisfied(
  field: Pick<
    EditTransactionField,
    'required' | 'requiredParameter' | 'rawValue' | 'status' | 'listPending'
  > &
    Partial<Pick<EditTransactionField, 'editable' | 'derivationState'>>,
): boolean {
  if (!field.required) return false
  if (field.listPending) return false
  // PVAL-26685: a read-only field its derivation is still filling — or failed to fill — is not
  // something the user can answer, so it does not hold Save. One that answered `null` is `idle`
  // and is judged like any other field.
  if (
    field.editable === false &&
    field.derivationState != null &&
    field.derivationState !== 'idle'
  ) {
    return false
  }
  return field.status === 'novalue' || requiredRuleUnmet(field)
}

/**
 * How many of a collection's LINE cells are short of what saving them would need.
 *
 * Three `arrayField` columns are curated `required`, so a collection's lines carry required fields
 * exactly as the dialog's own rows do — and the items modal already marks a blank one
 * (`ci-input-missing`). Leaving them out of the verdict would let the dialog report itself savable
 * while rendering a cell it has itself flagged as missing, which is the button-versus-behaviour
 * contradiction {@link requiredUnsatisfied} exists to prevent, one level down.
 *
 * Counted per CELL, not per line: two blank required cells on one line are two things to fix, which
 * is what the footer note is counting.
 *
 * `rows` carries display strings aligned to `columns` (the items modal writes edits back in that
 * same shape), so blankness is judged on the rendered cell — the thing the user is looking at.
 *
 * A collection with NO lines contributes nothing. There is no line to be incomplete, and treating an
 * empty collection as a missing line would refuse Save on every dialog whose row never carried the
 * array at all — which in edit mode is most of them, for the read-side/write-side reason on
 * `rawValueOf`. No parameterised rule applies here: none is authored on any `arrayField`, and the
 * config spec rejects a parameter authored without its flag.
 */
export function collectionRequiredUnsatisfied(
  collection: Pick<EditTransactionCollection, 'columns' | 'rows'>,
): number {
  const requiredColumns = collection.columns
    .map((column, index) => ({ column, index }))
    // A column whose list is declared but unresolvable renders disabled in the items modal, so its
    // blank is not a cell anyone can fill this phase — the same exemption `requiredUnsatisfied` makes,
    // for the same reason (PVAL-26493).
    .filter(entry => entry.column.required && !entry.column.listPending)
  if (!requiredColumns.length) return 0
  return collection.rows.reduce(
    (total, row) =>
      total + requiredColumns.filter(entry => isBlank(row[entry.index])).length,
    0,
  )
}

const linesOf = (row: WorkbenchRow | null, collection: string): Line[] => {
  const raw = row?.fields[collection]
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (entry): entry is Line => typeof entry === 'object' && entry !== null,
  )
}

/**
 * Collapse `lines` to one value by `rule`, reading whichever line-level key `target` names.
 *
 *  - `same`    — every line agrees, so the first answer stands for all of them. This is the default
 *                the design gives a field, which is why it is the fallback here too.
 *  - `count`   — how many lines there are, not what they hold.
 *  - `sum`     — add every line.
 *  - `condSum` — add only the lines whose condition field is one of the configured values: the GL
 *                distribution case, summing amount only where the account subtype is a cash subtype.
 *
 * The rule and the target arrive as SEPARATE arguments because they do not always come from the same
 * field. An ordinary field owns both halves — its own Grouping Logic, aggregating its own key (see
 * {@link groupedValue}). An Array Summary owns only the aggregation: the number it stands for is a
 * total of the collection's ADDING COLUMNS, so the target is the column's key while the rule is the
 * summary's own (see {@link EditTransactionViewModel}'s summary rows and the `summaryValue` closure).
 * Folding the two together is what made a summary's authored Grouping Logic unreadable — the value
 * shown was decided entirely by a different field's rule.
 */
const aggregate = (
  rule: GroupingRule,
  target: string,
  lines: Line[],
): unknown => {
  if (!lines.length) return undefined

  if (rule.logic === 'count') return lines.length

  const key = lineKey(target)

  if (rule.logic === 'same') {
    return lines.map(line => line[key]).find(value => !isBlank(value))
  }

  const condition = rule.conditionField
  const allowed = rule.conditionValues ?? []
  // The condition is a line-level key like any other, so it resolves through `lineKey` too. The
  // config currently spells it as the bare leaf (`accountSubType`), but its sibling key in the same
  // field block is the dotted `distributionLines.amount`; a curator following that convention writes
  // `distributionLines.accountSubType`, and reading the line by the dotted path matches nothing —
  // every line is filtered out and the conditional sum reduces to 0 with nothing to show for it.
  // Line-key resolution has to be total, not per-key.
  const eligible =
    rule.logic === 'condSum' && condition
      ? lines.filter(line =>
          allowed.includes(String(line[lineKey(condition)] ?? '')),
        )
      : lines

  return eligible.reduce((sum, line) => {
    const numeric = Number(line[key])
    return sum + (Number.isFinite(numeric) ? numeric : 0)
  }, 0)
}

/**
 * Collapse the collection's lines by the field's OWN Grouping Logic, over its OWN key.
 *
 * This is the ordinary case: a `signedSplit` GL amount, or a column of an items grid, aggregating
 * the key it is named for. A field with no rule authored has nothing to say about a collection.
 */
const groupedValue = (
  meta: TransactionFieldMetadata,
  lines: Line[],
): unknown => {
  const rule = meta.settings.grouping
  return rule ? aggregate(rule, meta.field, lines) : undefined
}

/**
 * The rule a summary applies to one of its collection's adding columns — summary-wins-else-inherit.
 *
 * The LOGIC is always the summary's: that is the whole point of a summary owning its aggregation.
 * The CONDITION is the summary's when it authors one, and the target column's when it does not.
 *
 * The condition is taken as a PAIR, never half from each side. Composing the two keys independently
 * (`rule.conditionField ?? inherited?.conditionField` alongside `rule.conditionValues ??
 * inherited?.conditionValues`) reads reasonably and is wrong: a summary carrying values but no field
 * — which is what a half-finished CRUD edit looks like — would then be evaluated against the
 * COLUMN's field with the SUMMARY's values, a rule neither of them authored. `conditionField` is
 * what decides whose condition is in force, and its values come with it.
 *
 * Inheritance exists so that authoring a condition on the summary does not have to RETIRE the
 * column's copy — the column's `condSum` still drives the Σ row under the lines in the items modal,
 * so both are live config rather than one curing dead config by creating more of it. The cost of two
 * copies is drift, and drift is caught at authoring time: `transaction-metadata.config.spec.ts`
 * asserts that a summary and its adding column, when both author a condition, author the SAME one.
 */
const summaryRuleFor = (
  rule: GroupingRule,
  column: TransactionFieldMetadata,
): GroupingRule => {
  const inherited = column.settings.grouping
  const condition = rule.conditionField != null ? rule : inherited
  return {
    logic: rule.logic,
    conditionField: condition?.conditionField,
    conditionValues: condition?.conditionValues,
  }
}

export function projectEditTransaction(
  input: EditTransactionInput,
): EditTransactionViewModel {
  if (input.resolution.kind === 'blocked') {
    return {
      mode: input.mode,
      title: BLOCK_HEADINGS[input.resolution.reason],
      detailLine: blockDetailLine(input.resolution),
      // No tran category was resolved, so there is no schema to name in a breadcrumb.
      breadcrumb: null,
      blocked: true,
      sections: [],
      footer: [],
      collections: [],
      // A blocked dialog renders no fields, so there is nothing to derive into.
      derivations: [],
      context: {},
      // A blocked dialog resolved no tran category, so it has no fields to read a source off.
      valueLabel: API_SOURCE.name,
      comparisonLabel: null,
      comparisonSources: [],
      statusCounts: { match: 0, diff: 0, partial: 0, novalue: 0 },
      canSave: false,
      rowId: input.row?.rowId ?? null,
      tranCategory: null,
      recordId: null,
    }
  }

  const tranCategoryKey: TranCategoryKey = input.resolution.tranCategory
  const definition = input.metadata.tranCategory(tranCategoryKey)
  const fields = input.metadata.fieldsFor(tranCategoryKey)
  const { mode, row, values } = input
  const createDefaults = values.createDefaults(tranCategoryKey)

  // Which Prometheus row (if any) the comparison column reads. In post mode the row being posted IS
  // the Prometheus row; in edit mode it is the matched counterpart the host handed us — the
  // server's `transactions/matches` answer first (PVAL-26451), then any in-session match.
  const counterpart =
    mode === 'post'
      ? row && isPrometheus(row.origin)
        ? row
        : null
      : input.counterparts.find(candidate => isPrometheus(candidate.origin)) ??
        null

  /**
   * The sources this record is compared against — **whichever ones the metadata maps a key for**.
   *
   * Read off the document rather than listed here: a Custody column exists because some field
   * declares a `prometheusCustodyKey`, a Trading column because some field declares a
   * `prometheusTradingKey`, and a Clearwater column because some field declares a `clearwaterKey`.
   * A hardcoded triple asserted three columns whether or not anything could ever fill them, so a
   * source nobody maps rendered as a permanently empty column that only the "Hide Absent Sources"
   * switch made tolerable.
   *
   * Order is the design's, not the document's — the columns read Custody, Trading, Clearwater
   * wherever they appear, so a record that happens to map only Trading does not shuffle the header
   * row relative to the one beside it.
   *
   * Whether a present column is SHOWN is still the switch's business: absent means "mapped, but
   * this record's source answered nothing", which is a different statement from "never mapped".
   */
  const comparisonOrigins: ComparisonOrigin[] = COMPARISON_ORIGINS.filter(
    origin =>
      fields.some(
        meta => meta.comparison?.[COMPARISON_KEYS[origin].modelKey] != null,
      ),
  )

  const comparisonOrigin: RowOrigin =
    counterpart?.origin ?? comparisonOrigins[0] ?? 'cust'
  const comparisonLabel =
    mode === 'create' ? null : TARGET_META[comparisonOrigin].label

  /** The matched row for one comparison source, or null when nothing matched it. */
  const counterpartFor = (origin: RowOrigin): WorkbenchRow | null => {
    if (mode === 'create') return null
    if (mode === 'post' && row?.origin === origin) return row
    return input.counterparts.find(entry => entry.origin === origin) ?? null
  }

  const counterpartByOrigin = new Map<RowOrigin, WorkbenchRow | null>(
    comparisonOrigins.map(origin => [origin, counterpartFor(origin)]),
  )

  /** The key this field answers to on a given source. */
  const keyForOrigin = (
    meta: TransactionFieldMetadata,
    origin: RowOrigin,
  ): string | undefined => {
    if (origin === 'cw') return meta.comparison?.clearwaterKey
    return comparisonKeyFor(meta, origin)
  }

  /**
   * The raw value shown in the field column.
   *
   * TODO(PVAL-26341): **Edit mode resolves only partially, by design-for-now.**
   *
   * The metadata layer keys fields by Enfusion's WRITE-side names — `quantity`, `price`,
   * `transactionType`, `counterpartyId` — because that is the schema a save posts to. A workbench
   * row carries the READ-side reporting names the grid catalog defines, and the two vocabularies
   * only partly coincide: of the twenty-one fields on a Listed trade, eight resolve off a row and
   * thirteen do not. Those thirteen render blank in Edit.
   *
   * That is the prototype's own model rather than a defect in this projection: the panel reads
   * report rows for the grid and, on opening a record, calls `GET Trade` / `GET GL Entry` for the
   * richer write-side detail the dialog describes. That call is the later backend ticket, so until
   * it exists Edit shows what the row happens to carry. Create is unaffected — it reads seeds, not a
   * row — and Post reads its counterpart through the comparison mapping, which is keyed
   * deliberately.
   *
   * Do NOT paper over this by aliasing metadata keys to grid names: it would put the dialog in two
   * vocabularies at once and quietly post read-side names to a write-side API.
   */
  const rawValueOf = (meta: TransactionFieldMetadata): unknown => {
    if (mode === 'create') return createDefaults[meta.field]
    // One definition, shared with the bulk grid's opening scan — see {@link openingRawValue}.
    return row ? openingRawValue(meta, row, mode) : undefined
  }

  /**
   * A mapped key the counterpart does not carry renders NOTHING.
   *
   * There is no fallback left to take: the sample column that used to fill this when no counterpart
   * matched was fabricated data, and it was removed along with its fixture. An empty cell now means
   * "nothing answered", which is the only thing it ever honestly meant.
   */
  const comparisonOf = (meta: TransactionFieldMetadata): unknown => {
    if (mode === 'create') return undefined
    const key = comparisonKeyFor(meta, comparisonOrigin)
    if (!key) return undefined
    return counterpart ? prometheusValueOf(counterpart, key) : undefined
  }

  /**
   * Whether an external record exists to compare against at all.
   *
   * Separate from whether a FIELD maps a key: one is about the record, the other about the field,
   * and conflating them is what made a row with no match read as half-answered.
   */
  const hasComparisonRecord = counterpart !== null

  /**
   * Whether this field has something to be compared against.
   *
   * **Two conditions, and the second is the one that used to be missing.** The metadata must map a
   * key for the field, AND an external record must exist to read that key off. Without a
   * counterpart there is nothing to disagree with, so `partial` and `diff` are not verdicts that
   * can be reached — a mapped field on an unmatched record is just a value, and reads `match`.
   *
   * Before this, a missing counterpart made every mapped field `partial`, which asserts "the
   * external source answered nothing" about a record that HAS no external source. The counts above
   * the grid then reported a page of half-answered rows on a trade nobody had matched yet.
   */
  const isMapped = (meta: TransactionFieldMetadata): boolean =>
    mode !== 'create' &&
    hasComparisonRecord &&
    !!comparisonKeyFor(meta, comparisonOrigin)

  /**
   * One formatted value per comparison source.
   *
   * A source with no matched row stays empty — there is no sample to borrow any more, which is what
   * lets "absent" mean the one thing it says.
   */
  const comparisonsOf = (
    meta: TransactionFieldMetadata,
  ): Partial<Record<RowOrigin, string | null>> => {
    const out: Partial<Record<RowOrigin, string | null>> = {}
    if (mode === 'create') return out
    comparisonOrigins.forEach(origin => {
      const key = keyForOrigin(meta, origin)
      const source = counterpartByOrigin.get(origin) ?? null
      const raw =
        key == null || !source
          ? undefined
          : // `cw` names a Clearwater key, which is a modeled field; the other two name blob keys.
          origin === 'cw'
          ? sourceValueOf(source, key)
          : prometheusValueOf(source, key)
      out[origin] = isBlank(raw)
        ? null
        : formatFieldValue(raw, meta.displayStyle)
    })
    return out
  }

  const toField = (
    meta: TransactionFieldMetadata,
    overrides: Partial<EditTransactionField> = {},
  ): EditTransactionField => {
    const raw = rawValueOf(meta)
    const comparison = comparisonOf(meta)
    const base: Omit<EditTransactionField, 'status'> = {
      id: meta.field,
      field: meta.field,
      label: meta.label,
      dataType: meta.dataType,
      style: meta.displayStyle,
      value: formatFieldValue(raw, meta.displayStyle),
      rawValue: raw,
      editable: meta.settings.edit,
      required: meta.settings.required === true,
      requiredHint: requiredHintFor(meta),
      requiredParameter: meta.settings.requiredParameter ?? null,
      listType: meta.listType ?? null,
      listValues: listOptionsOf(meta, input.onBlankCell?.(meta.field)),
      displayColumns: displayColumnsOf(meta),
      listKeys: listDisplayKeys(meta.listValues),
      listPending: listPendingOf(meta),
      minChars: listValueMinChars(meta.listValues),
      derivationState: 'idle',
      comparison: isBlank(comparison)
        ? null
        : formatFieldValue(comparison, meta.displayStyle),
      comparisons: comparisonsOf(meta),
      comparisonMapped: isMapped(meta),
      ...overrides,
    }
    // Derived AFTER overrides, never from the base values: the two halves of a signed split carry
    // their own value and their own comparison, so a +500 record against a -500 counterpart has to
    // read as two half-answered rows rather than one matching one.
    return { ...base, status: deriveFieldStatus(base) }
  }

  /**
   * The `+|- Split` container: one signed API field displayed as separate Debit / Credit fields.
   * A positive value is a debit, a negative one a credit; the comparison value follows its own sign
   * so a debit is never compared against a credit.
   *
   * Each side is routed independently, which is the whole point: the field's own value populates one
   * half, and the counterpart's value populates whichever half ITS sign belongs to. When the two
   * disagree in sign those are different halves — a +500 record against a -500 counterpart puts
   * `500.00` on the Debit row's field and `500.00` on the Credit row's comparison, with the other
   * cell of each pair empty. That reads as the sign disagreement it is; collapsing it (suppressing
   * the comparison because that half's own field is empty) would hide a real break, and pairing them
   * on one row would state the false one — that a debit and a credit are the same posting compared.
   */
  const splitFields = (
    meta: TransactionFieldMetadata,
    lines: Line[],
  ): EditTransactionField[] => {
    const grouped = groupedValue(meta, lines)
    const raw = grouped === undefined ? rawValueOf(meta) : grouped
    const numeric = Number(raw)
    const signed = Number.isFinite(numeric) ? numeric : 0
    const comparison = comparisonOf(meta)
    const comparisonNumeric = Number(comparison)
    const comparisonSigned = Number.isFinite(comparisonNumeric)
      ? comparisonNumeric
      : null
    const labels = meta.split ?? { debitLabel: 'Debit', creditLabel: 'Credit' }

    const half = (
      side: 'debit' | 'credit',
      label: string,
      amount: number | null,
    ): EditTransactionField =>
      toField(meta, {
        id: `${meta.field}:${side}`,
        label,
        rawValue: amount,
        value:
          amount === null ? '' : formatFieldValue(amount, meta.displayStyle),
        comparison:
          comparisonSigned === null ||
          isBlank(comparison) ||
          (side === 'debit' ? comparisonSigned < 0 : comparisonSigned >= 0)
            ? null
            : formatFieldValue(Math.abs(comparisonSigned), meta.displayStyle),
      })

    return [
      half('debit', labels.debitLabel, signed >= 0 ? signed : null),
      half('credit', labels.creditLabel, signed < 0 ? Math.abs(signed) : null),
    ]
  }

  /**
   * The one value that stands for a whole collection: the TARGET comes from the collection's adding
   * columns, the AGGREGATION from the summary's own authored Grouping Logic.
   *
   * Neither half can come from the other place. The target cannot be the summary's own key — a line
   * carries `amount`, never `distributionLines`, so summing the parent's key read nothing and every
   * summary came out as zero. And the aggregation cannot be the columns' — a summary that authored
   * `sum` while its adding column authored `condSum` rendered the CONDITIONAL total, so editing the
   * summary's Grouping Logic changed nothing and the value shown was decided by a different field's
   * rule. That is precisely what this layer promises not to do: a value read off the config page
   * means the same thing here.
   *
   * So the split is: the columns say WHAT there is to add, the summary says HOW.
   *
   *  - `count` needs no target at all — it is a fact about the lines, not about a datapoint. This is
   *    what Keywords authors, and the only truthful rule for a collection with nothing to add.
   *  - `sum` / `condSum` add each adding column's target, under the summary's own logic and its
   *    condition (inherited from the column when the summary authors none — {@link summaryRuleFor}).
   *  - `same` reads the first line that answers, over the same targets.
   *  - a summary that authors NO rule keeps the pre-existing derivation: the columns' own rules,
   *    falling back to the item count. Nothing in the Phase 1 config is authored that way, but the
   *    metadata layer is about to become editable through a CRUD API, and a summary saved without a
   *    Grouping Logic should still read as something rather than as blank.
   *
   * A `sum`/`condSum` summary over a collection with NO adding column has nothing to total, so it
   * renders blank rather than silently reporting the line COUNT as if it were a total — the
   * silent-wrong-figure this file's docstrings are written against. The config spec rejects that
   * authoring outright, so the blank is a backstop, not a behaviour a curator can reach.
   *
   * `collection` is the caller's already-resolved {@link collectionKeyOf} result, passed in rather
   * than re-derived here from `meta.field`. Re-deriving is what made the collection key two rules
   * that had to agree, and the count-instead-of-sum fallback above is exactly what a disagreement
   * silently produces.
   */
  /**
   * What a collection summary READS as, which is not always what it counts.
   *
   * A `count` summary is a number of lines, and a bare "2" in a value cell is indistinguishable
   * from a quantity or an id — the one thing the cell must say is what the number is OF. Every
   * other grouping logic is a real figure in the collection's own units and is formatted by the
   * field's display style like any other value.
   *
   * `rawValue` keeps the number; only the text changes. The status dot and any comparison still
   * read the count itself rather than this sentence.
   */
  const summaryText = (
    meta: TransactionFieldMetadata,
    summarised: unknown,
  ): string => {
    if (
      meta.settings.grouping?.logic !== 'count' ||
      typeof summarised !== 'number'
    ) {
      return formatFieldValue(summarised, meta.displayStyle)
    }
    return `${summarised} item${summarised === 1 ? '' : 's'}`
  }

  const summaryValue = (
    meta: TransactionFieldMetadata,
    collection: string,
    lines: Line[],
  ): unknown => {
    if (!lines.length) return undefined
    const adding = fields.filter(
      column =>
        column.collection === collection &&
        column.displayContainer === 'arrayField' &&
        (column.settings.grouping?.logic === 'sum' ||
          column.settings.grouping?.logic === 'condSum'),
    )

    const rule = meta.settings.grouping
    if (!rule) {
      if (!adding.length) return lines.length
      return adding.reduce((total, column) => {
        const value = Number(groupedValue(column, lines))
        return total + (Number.isFinite(value) ? value : 0)
      }, 0)
    }

    if (rule.logic === 'count') return lines.length
    if (!adding.length) return undefined

    if (rule.logic === 'same') {
      return adding
        .map(column => aggregate(rule, column.field, lines))
        .find(value => !isBlank(value))
    }

    return adding.reduce((total, column) => {
      const value = Number(
        aggregate(summaryRuleFor(rule, column), column.field, lines),
      )
      return total + (Number.isFinite(value) ? value : 0)
    }, 0)
  }

  const sections: EditTransactionSection[] = []
  /**
   * The section a field belongs to.
   *
   * An empty Display Group is not "no group" — it means *continue the one above*. The metadata layer
   * uses it heavily: `irsType`, `startDate` and `terminationDate` carry no group of their own and are
   * meant to read under the "Trade details" heading that `transactionType` opened. Treating blank as
   * its own section would put an unnamed band in the middle of every schema-specific dialog.
   *
   * Fields arrive in `order` order, so "the one above" is simply the last group named.
   */
  let lastGroup = ''
  const sectionFor = (name: string): EditTransactionSection => {
    if (name) lastGroup = name
    const resolved = name || lastGroup
    const existing = sections.find(section => section.name === resolved)
    if (existing) return existing
    const created: EditTransactionSection = { name: resolved, fields: [] }
    sections.push(created)
    return created
  }

  const footer: EditTransactionField[] = []

  // Structure decides WHERE a field goes; `present` then overrides HOW it is drawn there. A field
  // presented in the footer leaves the row list entirely; one presented as a signed split becomes
  // two fields in the section it already belonged to.
  fields.forEach(meta => {
    // Every collection read goes through `collectionKeyOf`, this one included — the lines a
    // `signedSplit` aggregates over are fetched by the same resolved key the summary rows use. Today
    // a split field always declares its collection explicitly, so this is the key it already named;
    // routing it through the helper anyway is what keeps the "resolve once" invariant total rather
    // than true-by-coincidence, which is the whole failure mode this commit exists to close. A field
    // that names no collection resolves to its own key and reads no array, so there is nothing left
    // for a guard to protect.
    const lines = linesOf(row, collectionKeyOf(meta))
    if (meta.present === 'footerField') {
      footer.push(toField(meta))
      return
    }
    switch (meta.displayContainer) {
      case 'default':
        if (meta.present === 'signedSplit') {
          sectionFor(meta.displayGroup).fields.push(...splitFields(meta, lines))
        } else {
          sectionFor(meta.displayGroup).fields.push(toField(meta))
        }
        break
      case 'arraySummary': {
        // A summary is a row of the dialog like any other, and sits in its OWN group at its own
        // order — `distributionLines` is "Journal entry" at 25, between the memo and the entry
        // status. Collecting these into a block rendered after the field list put them under
        // whichever section happened to end last, which is how Distribution Lines came to appear
        // under References. Only the collection's COLUMNS belong to the items modal.
        //
        // One collection key, resolved once, used for both the row read and the column match.
        const collection = collectionKeyOf(meta)
        const summarised = summaryValue(
          meta,
          collection,
          linesOf(row, collection),
        )
        sectionFor(meta.displayGroup).fields.push(
          toField(meta, {
            rawValue: summarised,
            value: summaryText(meta, summarised),
          }),
        )
        break
      }
      case 'arrayField':
        // Columns of a collection, assembled below for its items modal.
        break
    }
  })

  /**
   * The collections this dialog can edit, DERIVED from its own `arraySummary` fields.
   *
   * These used to come from `TranCategoryDefinition.collections`, which made a collection two facts that
   * had to agree: the summary ROW came from the field passing the key-group filter, while the items
   * MODAL came from the tran category naming it. They had already drifted — `otherPayments` and `keywords`
   * are in `common`, so all fourteen trade tran categories render their summary rows, but only
   * `listedTrade` declared them; `nearLeg` and `farLeg` were declared by nothing at all. On those
   * dialogs `collectionFor()` returned null and the template fell through to `#plainValue`,
   * rendering an array summary as a bare text box with no grid icon at all.
   *
   * Deriving makes that unrepresentable: the row and the modal now come from the same field, so one
   * cannot exist without the other. `label` is the summary field's own — identical to what the
   * tran categories declared — and `order` order is preserved because `fields` arrives ord-sorted.
   */
  const collections: EditTransactionCollection[] = fields
    .filter(meta => meta.displayContainer === 'arraySummary')
    .map(summary => {
      // The collection key comes from `collectionKeyOf` — the SAME derivation the summary row above
      // used — so the row's array, the column match and the aggregate can never be selected by
      // three different keys. A summary names its own collection in the Phase 1 config, so the
      // declared name and the field coincide today; the metadata layer is about to become editable
      // through a CRUD API, and on the day they diverge this still resolves.
      const collectionName = collectionKeyOf(summary)
      const lines = linesOf(row, collectionName)
      const columnMeta = fields.filter(
        meta =>
          meta.displayContainer === 'arrayField' &&
          meta.collection === collectionName,
      )

      return {
        // Keyed by the summary FIELD, because that is what `collectionFor(field)` looks it up by.
        name: summary.field,
        label: summary.label,
        editable: summary.settings.edit,
        summarise: rows => {
          const rawValue = summaryValue(
            summary,
            collectionName,
            rows.map(cells => lineFromCells(columnMeta, cells)),
          )
          return { rawValue, value: summaryText(summary, rawValue) }
        },
        columns: columnMeta.map(meta => ({
          field: meta.field,
          label: meta.label,
          dataType: meta.dataType,
          style: meta.displayStyle,
          editable: meta.settings.edit,
          required: meta.settings.required === true,
          listType: meta.listType ?? null,
          // The IDENTICAL path a top-level field resolves by, so a line's currency is a picker in the
          // items modal for exactly the reason it would be in the dialog.
          listValues: listOptionsOf(meta, input.onBlankCell?.(meta.field)),
          displayColumns: displayColumnsOf(meta),
          listKeys: listDisplayKeys(meta.listValues),
          listPending: listPendingOf(meta),
          minChars: listValueMinChars(meta.listValues),
          sums:
            meta.settings.grouping?.logic === 'sum' ||
            meta.settings.grouping?.logic === 'condSum',
        })),
        rows: lines.map(line =>
          columnMeta.map(meta =>
            formatFieldValue(line[lineKey(meta.field)], meta.displayStyle),
          ),
        ),
        // Per-line derivations: a column of this collection derived from a sibling on its own line.
        derivations: derivationsOf(columnMeta),
        // PVAL-26440: what the save needs and the rows above have formatted away.
        dataType: summary.dataType,
        key: collectionName,
        originalLines: lines.map(line => ({ ...line })),
        lineOrigins: lines.map((_, index) => index),
        // An `object` container has no lines — `linesOf` reads only an array — so its children are
        // read by path, exactly as a top-level dotted field is, for the save to write back by path.
        ...(summary.dataType === 'object'
          ? {
              objectValues: Object.fromEntries(
                columnMeta.map(meta => [meta.field, rawValueOf(meta)]),
              ),
            }
          : {}),
      }
    })

  /**
   * The title names the ACTION, not the schema — "New Transaction", not "Create Interest Rate Swap
   * Trade". Which schema is being built is the breadcrumb's job ("Enfusion · Interest Rate Swap ·
   * Account ID 505534"), so putting it in the title too would say it twice and give the dialog a
   * heading that changes length with every tran category.
   */
  /**
   * The comparison columns and which of them said nothing.
   *
   * Absence is judged across every projected field rather than per row: a source that answered one
   * field out of thirty is present — sparsely, which the per-row status already reports — while one
   * that answered none of them is the column the switch is there to hide.
   *
   * Create mode is included rather than skipped. A draft has matched nothing, so all three read as
   * absent and the dialog opens on the Enfusion column alone — which is what the design shows — but
   * the columns still EXIST, so the switch is there to reveal them. Returning an empty list instead
   * conflated "nothing matched" with "this dialog has no comparison sources" and took the switch off
   * the create dialog entirely.
   */
  const buildComparisonSources = (): EditTransactionComparisonSource[] => {
    const projected = [
      ...sections.flatMap(section => section.fields),
      ...footer,
    ]
    return comparisonOrigins.map(origin => ({
      origin,
      label: comparisonKeyLabel(COMPARISON_KEYS[origin].documentKey),
      // A source with nothing in it is ABSENT, and the on-by-default "Hide Absent Sources" switch
      // hides it — on an edit with no matched counterpart for that source, its column stays out of
      // the way rather than standing empty and pushing the Enfusion values off to the side.
      // Unchecking the switch still shows it — it is a viewing preference, not a claim about the
      // record.
      absent: !projected.some(field => field.comparisons[origin] != null),
    }))
  }

  const title =
    mode === 'create'
      ? 'New Transaction'
      : mode === 'post'
      ? `Post to ${API_SOURCE.name}`
      : 'Edit Transaction'

  // Every field the dialog shows, in one list — the population the tallies count and the Req rules
  // are judged over. Derived once so the status counts and the Save verdict can never be read off
  // two differently-assembled populations.
  const shownFields = [
    ...sections.flatMap(section => section.fields),
    ...footer,
  ]

  return {
    mode,
    title,
    detailLine:
      mode === 'post'
        ? `Seeded from the matched ${TARGET_META[comparisonOrigin].label} transaction`
        : null,
    breadcrumb: {
      source: API_SOURCE.name,
      // The tran category's own name, taken off its definition so it reads as the design does ("Listed",
      // not "Listed Trade" — the title already says which verb applies).
      tranCategory: definition?.name ?? tranCategoryKey,
      account: accountLabel(row, input.accountId),
    },
    blocked: false,
    sections,
    footer,
    collections,
    // Only the fields this tran category composes: a derivation on a field the dialog does not
    // render has nothing to fill, and would sit in the trigger graph waiting on a key nobody edits.
    // Collection columns are excluded — their derivations run per line, in the items modal.
    derivations: derivationsOf(
      fields.filter(meta => meta.displayContainer !== 'arrayField'),
    ),
    // The RAW account id — what every `/resolve` item's `params.accountId`, a `trades/get` read and
    // a context input all send. The chip above reads the same id dressed for display.
    context: { accountId: accountIdOf(row, input.accountId) },
    valueLabel: sourceLabelOf(input.metadata.source),
    comparisonLabel,
    comparisonSources: buildComparisonSources(),
    statusCounts: tallyStatuses(shownFields),
    // "Req — required to save": a record that arrives with a required field unanswered, or with a
    // value breaking a rule the dialog is rendering, cannot be offered as savable. Judged over the
    // collections' lines as well as the dialog's own rows, so a blank required cell in an items grid
    // is not a way around the verdict.
    canSave:
      !shownFields.some(requiredUnsatisfied) &&
      !collections.some(
        collection => collectionRequiredUnsatisfied(collection) > 0,
      ),
    rowId: row?.rowId ?? null,
    tranCategory: tranCategoryKey,
    recordId: mode === 'edit' && row ? recordIdOf(row.fields) : null,
  }
}

/**
 * A field's standing against its comparison source, from the field alone.
 *
 * Exported because the modal re-derives it as the user types: the projection decides the opening
 * state, and every edit after that has to re-answer the same question. Keeping one implementation
 * means a typed value and a projected value are judged by identical rules.
 *
 * Note this is a *comparison* verdict, not a validation one — it says whether the sources agree,
 * not whether the value is legal. See the validation TODO in the modal's `onEdit`.
 */
export function deriveFieldStatus(
  field: Pick<
    EditTransactionField,
    'rawValue' | 'value' | 'comparison' | 'comparisonMapped'
  >,
): EditTransactionFieldStatus {
  if (isBlank(field.rawValue)) return 'novalue'
  // An unmapped field has nothing to disagree with, so it can never read as a difference.
  if (!field.comparisonMapped) return 'match'
  if (field.comparison === null) return 'partial'
  return field.comparison === field.value ? 'match' : 'diff'
}

/**
 * The account chip's text — "Account ID 505534" for the record's own account, "Session account
 * 505534" when the value did not come off the record.
 *
 * BC-7. The account is a property of the RECORD being edited, not of the schema, so it is read off
 * the row. But a create draft has no row at all, and a row can arrive without an account, so
 * something stands in — and what stands in is the workbench's focused account, which is
 * `selectFirstLoadedAccountId`: it silently picks ONE of the user's loaded accounts. For a
 * single-account user that is the right answer. For a multi-account user it is an arbitrary
 * first-loaded one, and a chip reading "Account ID 505534" would assert the record belongs to it.
 * With no account to stand in at all there is no chip, rather than one naming an account nobody
 * chose.
 *
 * So the two cases are labelled differently rather than blended, because no sentinel value can tell
 * them apart downstream — the distinction has to be drawn here, while the row is still in hand. The
 * stand-in wording is deliberately NON-POSSESSIVE: "Session account", matching
 * `create-transactions-modal`'s own vocabulary, and never "Your account" or "My account". It names
 * where the value came from and claims nothing about whose it is or which record it belongs to.
 */
function accountLabel(
  row: WorkbenchRow | null,
  standIn?: string | null,
): string | null {
  // The row model carries the account as a first-class property (the adapter fills it from the
  // response's own `accountId`); the field map is the fallback for a row built field-first.
  const recordAccountId = isBlank(row?.accountId)
    ? row?.fields['accountId']
    : row?.accountId

  if (!isBlank(recordAccountId)) return `Account ID ${String(recordAccountId)}`
  return isBlank(standIn) ? null : `Session account ${String(standIn).trim()}`
}

/** Tally every shown field by status, for the summary bar above the grid. */
function tallyStatuses(
  fields: EditTransactionField[],
): EditTransactionStatusCounts {
  const counts: EditTransactionStatusCounts = {
    match: 0,
    diff: 0,
    partial: 0,
    novalue: 0,
  }
  fields.forEach(field => {
    counts[field.status] += 1
  })
  return counts
}
